import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, ArrowRight, Store, TrendingUp, TrendingDown } from "lucide-react";
import { inr } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Button } from "@/components/ui/button";

export default function CartPage() {
  const { cart, updateQty, toggleSave } = useCart();
  const nav = useNavigate();

  const bySeller = cart.items.reduce((acc, it) => {
    (acc[it.seller_name] = acc[it.seller_name] || []).push(it);
    return acc;
  }, {});
  const subtotal = cart.items.reduce((s, i) => s + i.price * i.qty, 0);
  const shipping = cart.items.reduce((s, i) => s + i.shipping_fee, 0);

  return (
    <div data-testid="cart-page" className="max-w-7xl mx-auto px-4 sm:px-8 mt-10">
      <h1 className="font-heading text-3xl font-bold tracking-tight">Shopping cart</h1>
      {!cart.items.length && !cart.saved.length && (
        <div data-testid="cart-empty" className="mt-10 rounded-2xl bg-slate-50 py-20 text-center">
          <p className="font-heading text-xl font-bold">Your cart is empty</p>
          <Button asChild className="mt-6 rounded-full bg-slate-900"><Link to="/search" data-testid="cart-empty-shop-btn">Continue shopping</Link></Button>
        </div>
      )}

      <div className="grid lg:grid-cols-[1fr_340px] gap-10 mt-8">
        <div className="space-y-8">
          {Object.entries(bySeller).map(([seller, items]) => (
            <div key={seller} data-testid={`cart-seller-group-${seller.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="rounded-2xl border border-slate-200 overflow-hidden">
              <p className="px-6 py-3 bg-slate-50 text-xs font-bold uppercase tracking-widest text-slate-500 flex items-center gap-2">
                <Store className="w-3.5 h-3.5" /> Sold by {seller}
              </p>
              {items.map((it) => (
                <div key={it.offer_id} data-testid={`cart-item-${it.offer_id}`} className="flex gap-5 p-6 border-t border-slate-100">
                  <Link to={`/product/${it.product_id}`} className="w-24 h-24 rounded-lg overflow-hidden bg-slate-100 shrink-0">
                    {it.image && <img src={it.image} alt={it.title} className="w-full h-full object-cover" />}
                  </Link>
                  <div className="flex-1 min-w-0">
                    <Link to={`/product/${it.product_id}`} className="font-semibold text-sm hover:text-blue-600 line-clamp-2">{it.title}</Link>
                    {it.price_changed && (
                      <p data-testid={`price-change-${it.offer_id}`} className="text-xs font-semibold text-amber-600 mt-1 inline-flex items-center gap-1">
                        {it.price > it.price_at_add ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                        Price changed since you added it
                      </p>
                    )}
                    {it.qty > it.stock && <p className="text-xs font-semibold text-red-500 mt-1">Only {it.stock} left — reduce quantity</p>}
                    <p className="text-xs text-slate-400 mt-1">Delivery in {it.delivery_days} days · {it.shipping_fee ? `${inr(it.shipping_fee)} shipping` : "Free shipping"}</p>
                    <div className="flex items-center gap-4 mt-3">
                      <div className="flex items-center border border-slate-200 rounded-full">
                        <button data-testid={`qty-minus-${it.offer_id}`} onClick={() => updateQty(it.offer_id, it.qty - 1)} className="p-2 hover:text-blue-600"><Minus className="w-3.5 h-3.5" /></button>
                        <span data-testid={`qty-${it.offer_id}`} className="w-8 text-center text-sm font-semibold">{it.qty}</span>
                        <button data-testid={`qty-plus-${it.offer_id}`} onClick={() => updateQty(it.offer_id, Math.min(it.qty + 1, it.stock))} className="p-2 hover:text-blue-600"><Plus className="w-3.5 h-3.5" /></button>
                      </div>
                      <button data-testid={`save-later-${it.offer_id}`} onClick={() => toggleSave(it.offer_id)} className="text-xs font-semibold text-slate-500 hover:text-blue-600">Save for later</button>
                      <button data-testid={`remove-${it.offer_id}`} onClick={() => updateQty(it.offer_id, 0)} className="text-xs font-semibold text-slate-500 hover:text-red-500 inline-flex items-center gap-1">
                        <Trash2 className="w-3.5 h-3.5" /> Remove
                      </button>
                    </div>
                  </div>
                  <p className="font-heading font-bold">{inr(it.price * it.qty)}</p>
                </div>
              ))}
            </div>
          ))}

          {cart.saved.length > 0 && (
            <div className="rounded-2xl border border-dashed border-slate-200 p-6">
              <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-4">Saved for later ({cart.saved.length})</p>
              {cart.saved.map((it) => (
                <div key={it.offer_id} data-testid={`saved-item-${it.offer_id}`} className="flex items-center gap-4 py-3 border-t border-slate-100 first:border-0">
                  <div className="w-14 h-14 rounded-lg overflow-hidden bg-slate-100">{it.image && <img src={it.image} alt="" className="w-full h-full object-cover" />}</div>
                  <p className="flex-1 text-sm font-medium line-clamp-1">{it.title}</p>
                  <p className="font-bold text-sm">{inr(it.price)}</p>
                  <button data-testid={`move-to-cart-${it.offer_id}`} onClick={() => toggleSave(it.offer_id)} className="text-xs font-semibold text-blue-600">Move to cart</button>
                </div>
              ))}
            </div>
          )}
        </div>

        {cart.items.length > 0 && (
          <aside data-testid="cart-summary" className="rounded-2xl border border-slate-200 p-6 h-fit sticky top-32">
            <p className="font-heading font-bold text-lg">Order summary</p>
            <div className="mt-4 space-y-2.5 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Subtotal ({cart.items.length} items)</span><span data-testid="cart-subtotal" className="font-semibold">{inr(subtotal)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Shipping</span><span className="font-semibold">{shipping ? inr(shipping) : "Free"}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Estimated tax (incl.)</span><span className="font-semibold">{inr(Math.round(subtotal * 0.05))}</span></div>
              <div className="border-t border-slate-100 pt-3 flex justify-between font-heading font-bold text-lg"><span>Total</span><span data-testid="cart-total">{inr(subtotal + shipping)}</span></div>
            </div>
            <Button data-testid="checkout-btn" onClick={() => nav("/checkout")} className="w-full mt-6 rounded-full h-12 bg-slate-900 hover:bg-slate-700 font-semibold">
              Proceed to checkout <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <p className="text-[11px] text-slate-400 mt-3 text-center">Multi-seller orders are split automatically — one checkout, separate tracked shipments.</p>
          </aside>
        )}
      </div>
    </div>
  );
}
